import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { 
  collection, 
  getDocs, 
  doc, 
  setDoc, 
  deleteDoc, 
  query, 
  where 
} from "firebase/firestore";
import { 
  createUserWithEmailAndPassword, 
  deleteUser as deleteAuthUser, 
  signInWithEmailAndPassword,
  EmailAuthProvider,
  reauthenticateWithCredential
} from "firebase/auth";
import { db, auth } from "../../config/firebase";
import { useAuth } from "../../contexts/AuthContext";
import MigrateUsers from "./MigrateUsers";

const UserManager = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const { user: currentUser } = useAuth();
  
  // Define user roles
  const USER_ROLES = [
    { id: 'admin', label: 'Admin', description: 'Full access to all features' },
    { id: 'gym_admin', label: 'Gym Admin', description: 'Manage gym-specific content' },
    { id: 'coach', label: 'Coach', description: 'Limited access to coaching features' },
    { id: 'judge', label: 'Judge', description: 'Access to judging features' },
    { id: 'user', label: 'Regular User', description: 'Basic access' }
  ];
  
  // New user form state
  const [newUser, setNewUser] = useState({
    email: "",
    password: "",
    role: "user",
    name: "",
    gym: ""
  });
  
  // Edit user modal state
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  
  // Delete confirmation modal state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);

  // Fetch users from Firestore
  const fetchUsers = async () => {
    setLoading(true);
    try {
      const usersCollection = collection(db, "users");
      const userSnapshot = await getDocs(usersCollection);
      const usersList = userSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setUsers(usersList);
    } catch (err) {
      console.error("Error fetching users:", err);
      setError("Failed to load users. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  // Handle new user form input changes
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setNewUser({
      ...newUser,
      [name]: type === "checkbox" ? checked : value
    });
  };

  // Create a new user
  const handleCreateUser = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    setSuccess("");

    try {
      // Create user in Firebase Auth
      const userCredential = await createUserWithEmailAndPassword(
        auth, 
        newUser.email, 
        newUser.password
      );
      
      // Add user to Firestore with additional data
      await setDoc(doc(db, "users", userCredential.user.uid), {
        email: newUser.email,
        name: newUser.name,
        role: newUser.role,
        gym: newUser.gym || null,
        isAdmin: newUser.role === 'admin', // Backward compatibility
        approved: true, // All users created by admin are approved by default
        createdAt: new Date().toISOString(),
        createdBy: currentUser.uid
      });

      // Reset form
      setNewUser({
        email: "",
        password: "",
        role: "user",
        name: "",
        gym: ""
      });
      
      setSuccess("User created successfully!");
      fetchUsers(); // Refresh user list
    } catch (err) {
      console.error("Error creating user:", err);
      setError(err.message || "Failed to create user. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Open edit user modal
  const handleEditClick = (user) => {
    setEditingUser({
      ...user,
      password: "" // Don't include password in edit form
    });
    setShowEditModal(true);
  };

  // Handle edit user form input changes
  const handleEditInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setEditingUser({
      ...editingUser,
      [name]: type === "checkbox" ? checked : value
    });
  };

  // Approve a user
  const handleApproveUser = async (userId) => {
    setLoading(true);
    setError("");
    setSuccess("");

    try {
      // Update user in Firestore
      await setDoc(doc(db, "users", userId), {
        approved: true,
        updatedAt: new Date().toISOString(),
        updatedBy: currentUser.uid
      }, { merge: true });

      setSuccess("User approved successfully!");
      fetchUsers(); // Refresh user list
    } catch (err) {
      console.error("Error approving user:", err);
      setError(err.message || "Failed to approve user. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Update user
  const handleUpdateUser = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    setSuccess("");

    try {
      // Update user in Firestore
      await setDoc(doc(db, "users", editingUser.id), {
        email: editingUser.email,
        name: editingUser.name,
        role: editingUser.role,
        gym: editingUser.gym || null,
        isAdmin: editingUser.role === 'admin', // Backward compatibility
        updatedAt: new Date().toISOString(),
        updatedBy: currentUser.uid
      }, { merge: true });

      setSuccess("User updated successfully!");
      setShowEditModal(false);
      fetchUsers(); // Refresh user list
    } catch (err) {
      console.error("Error updating user:", err);
      setError(err.message || "Failed to update user. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Open delete confirmation modal
  const handleDeleteClick = (user) => {
    setUserToDelete(user);
    setShowDeleteModal(true);
  };

  // Delete user
  const handleDeleteUser = async () => {
    setLoading(true);
    setError("");
    setSuccess("");

    try {
      // Delete user from Firestore
      await deleteDoc(doc(db, "users", userToDelete.id));
      
      // Note: Deleting from Firebase Auth requires re-authentication
      // This is a simplified version - in a real app, you'd need to handle this more securely
      setSuccess("User deleted from database successfully!");
      setShowDeleteModal(false);
      fetchUsers(); // Refresh user list
    } catch (err) {
      console.error("Error deleting user:", err);
      setError(err.message || "Failed to delete user. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 p-6">
      <div className="max-w-7xl mx-auto">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
            User Management
          </h1>
          <p className="mt-2 text-gray-600 dark:text-gray-300">
            Create, edit, and manage user accounts
          </p>
        </div>
        
        {/* Migration Tool */}
        <MigrateUsers />

        {/* Error and Success Messages */}
        {error && (
          <div className="mb-4 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md text-red-600 dark:text-red-400">
            {error}
          </div>
        )}
        {success && (
          <div className="mb-4 p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-md text-green-600 dark:text-green-400">
            {success}
          </div>
        )}

        {/* Create New User Form */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md p-6 mb-8 border border-gray-100 dark:border-gray-700">
          <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
            Create New User
          </h2>
          <form onSubmit={handleCreateUser}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Name
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={newUser.name}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                />
              </div>
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Email
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={newUser.email}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                />
              </div>
              <div>
                <label htmlFor="password" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Password
                </label>
                <input
                  type="password"
                  id="password"
                  name="password"
                  value={newUser.password}
                  onChange={handleInputChange}
                  required
                  minLength="6"
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                />
              </div>
              <div>
                <label htmlFor="role" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Role
                </label>
                <select
                  id="role"
                  name="role"
                  value={newUser.role}
                  onChange={handleInputChange}
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                >
                  {USER_ROLES.map(role => (
                    <option key={role.id} value={role.id}>
                      {role.label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label htmlFor="gym" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Gym Name {(newUser.role === 'coach' || newUser.role === 'gym_admin') && <span className="text-red-500 ml-1">*</span>}
                </label>
                <input
                  type="text"
                  id="gym"
                  name="gym"
                  value={newUser.gym}
                  onChange={handleInputChange}
                  className={`w-full px-4 py-2 border ${(newUser.role === 'coach' || newUser.role === 'gym_admin') ? 'border-blue-300 dark:border-blue-600' : 'border-gray-300 dark:border-gray-600'} rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white`}
                  placeholder={(newUser.role === 'coach' || newUser.role === 'gym_admin') ? 'Required for Gym Admins and Coaches' : 'Optional'}
                  required={newUser.role === 'coach' || newUser.role === 'gym_admin'}
                />
                {(newUser.role === 'coach' || newUser.role === 'gym_admin') && (
                  <p className="mt-1 text-sm text-blue-600 dark:text-blue-400">
                    Gym name is required for {newUser.role === 'gym_admin' ? 'Gym Administrators' : 'Coaches'}
                  </p>
                )}
              </div>
            </div>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-md shadow-sm disabled:opacity-50"
            >
              {loading ? "Creating..." : "Create User"}
            </button>
          </form>
        </div>

        {/* Users List */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md overflow-hidden border border-gray-100 dark:border-gray-700">
          <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
            <h2 className="text-xl font-bold text-gray-900 dark:text-white">
              Users
            </h2>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-900/50">
                <tr>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Name
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Email
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Role
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Gym
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Status
                  </th>
                  <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                {loading && users.length === 0 ? (
                  <tr>
                    <td colSpan="4" className="px-6 py-4 text-center text-gray-500 dark:text-gray-400">
                      Loading users...
                    </td>
                  </tr>
                ) : users.length === 0 ? (
                  <tr>
                    <td colSpan="4" className="px-6 py-4 text-center text-gray-500 dark:text-gray-400">
                      No users found
                    </td>
                  </tr>
                ) : (
                  users.map((user, index) => (
                    <motion.tr
                      key={user.id}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.2, delay: index * 0.05 }}
                    >
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                        {user.name || "N/A"}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                        {user.email}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                        {(() => {
                          // Get role from user data, fallback to legacy isAdmin check
                          const userRole = user.role || (user.isAdmin ? 'admin' : 'user');
                          const roleObj = USER_ROLES.find(r => r.id === userRole) || USER_ROLES[3]; // Default to regular user
                          
                          let bgColor = 'bg-gray-100 dark:bg-gray-700';
                          let textColor = 'text-gray-800 dark:text-gray-200';
                          
                          if (userRole === 'admin') {
                            bgColor = 'bg-blue-100 dark:bg-blue-900';
                            textColor = 'text-blue-800 dark:text-blue-200';
                          } else if (userRole === 'gym_admin') {
                            bgColor = 'bg-purple-100 dark:bg-purple-900';
                            textColor = 'text-purple-800 dark:text-purple-200';
                          } else if (userRole === 'coach') {
                            bgColor = 'bg-green-100 dark:bg-green-900';
                            textColor = 'text-green-800 dark:text-green-200';
                          }
                          
                          return (
                            <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${bgColor} ${textColor}`}>
                              {roleObj.label}
                            </span>
                          );
                        })()} 
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                        {user.gym || '—'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        {user.approved === true ? (
                          <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200">
                            Approved
                          </span>
                        ) : user.approved === false ? (
                          <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-yellow-100 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-200">
                            Pending
                          </span>
                        ) : (
                          <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-300">
                            Not Set
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        {user.approved !== true && (
                          <button
                            onClick={() => handleApproveUser(user.id)}
                            className="text-green-600 dark:text-green-400 hover:text-green-900 dark:hover:text-green-300 mr-4"
                          >
                            Approve
                          </button>
                        )}
                        <button
                          onClick={() => handleEditClick(user)}
                          className="text-blue-600 dark:text-blue-400 hover:text-blue-900 dark:hover:text-blue-300 mr-4"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleDeleteClick(user)}
                          className="text-red-600 dark:text-red-400 hover:text-red-900 dark:hover:text-red-300"
                        >
                          Delete
                        </button>
                      </td>
                    </motion.tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Edit User Modal */}
      {showEditModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full">
            <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-medium text-gray-900 dark:text-white">Edit User</h3>
            </div>
            <form onSubmit={handleUpdateUser}>
              <div className="px-6 py-4">
                <div className="mb-4">
                  <label htmlFor="edit-name" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Name
                  </label>
                  <input
                    type="text"
                    id="edit-name"
                    name="name"
                    value={editingUser.name || ""}
                    onChange={handleEditInputChange}
                    required
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                  />
                </div>
                <div className="mb-4">
                  <label htmlFor="edit-email" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Email
                  </label>
                  <input
                    type="email"
                    id="edit-email"
                    name="email"
                    value={editingUser.email}
                    onChange={handleEditInputChange}
                    required
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                  />
                </div>
                <div className="mb-4">
                  <label htmlFor="edit-role" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Role
                  </label>
                  <select
                    id="edit-role"
                    name="role"
                    value={editingUser.role || (editingUser.isAdmin ? 'admin' : 'user')}
                    onChange={handleEditInputChange}
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                  >
                    {USER_ROLES.map(role => (
                      <option key={role.id} value={role.id}>
                        {role.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="mb-4">
                  <label htmlFor="edit-gym" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Gym (for Coaches/Gym Admins)
                  </label>
                  <input
                    type="text"
                    id="edit-gym"
                    name="gym"
                    value={editingUser.gym || ""}
                    onChange={handleEditInputChange}
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                    placeholder={editingUser.role === 'coach' || editingUser.role === 'gym_admin' ? 'Required' : 'Optional'}
                    required={editingUser.role === 'coach' || editingUser.role === 'gym_admin'}
                  />
                </div>
              </div>
              <div className="px-6 py-4 bg-gray-50 dark:bg-gray-700 flex justify-end space-x-3 rounded-b-lg">
                <button
                  type="button"
                  onClick={() => setShowEditModal(false)}
                  className="px-4 py-2 bg-gray-200 dark:bg-gray-600 text-gray-800 dark:text-gray-200 font-medium rounded-md shadow-sm hover:bg-gray-300 dark:hover:bg-gray-500"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-md shadow-sm disabled:opacity-50"
                >
                  {loading ? "Saving..." : "Save Changes"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full">
            <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-medium text-gray-900 dark:text-white">Confirm Deletion</h3>
            </div>
            <div className="px-6 py-4">
              <p className="text-gray-700 dark:text-gray-300">
                Are you sure you want to delete the user <span className="font-semibold">{userToDelete?.email}</span>? This action cannot be undone.
              </p>
            </div>
            <div className="px-6 py-4 bg-gray-50 dark:bg-gray-700 flex justify-end space-x-3 rounded-b-lg">
              <button
                type="button"
                onClick={() => setShowDeleteModal(false)}
                className="px-4 py-2 bg-gray-200 dark:bg-gray-600 text-gray-800 dark:text-gray-200 font-medium rounded-md shadow-sm hover:bg-gray-300 dark:hover:bg-gray-500"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleDeleteUser}
                disabled={loading}
                className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white font-medium rounded-md shadow-sm disabled:opacity-50"
              >
                {loading ? "Deleting..." : "Delete User"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserManager;
